import { DocumentRequest } from '@/data/types/api';
import { NS_REVIEW } from '@/i18n/namespaces';
import { OpenPDF, isBase64PDF } from '@/utils/document';
import { getPreferredLang } from '@/utils/preferredLang';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostDocumentMutation } from '../apis';
import { DocumentType, Language, Locale } from '../enums';
import { useAppSelector } from '../reducers';
import { useRoute } from './useRoute';

export const useDocument = () => {
  const [postDocument, { isLoading }] = usePostDocumentMutation();
  const { i18n } = useTranslation(NS_REVIEW);
  const { redirect } = useRoute();

  const application = useAppSelector((state) => state.application);
  const documentEmail =
    application.formValues.application?.policy_owner
      .personalDetail.email ??
    application.formValues.quotation?.policy_owner
      ?.email ??
    application.formValues.quotation?.life_insured
      ?.email ??
    '';
  const documentCacheRef = useRef(
    new Map<DocumentType, string>(),
  );
  const inFlightRequestRef = useRef(
    new Map<DocumentType, Promise<string | null>>(),
  );

  const fetchDocument = useCallback(
    async (
      documentType: DocumentType,
      redirectOnError: boolean,
    ): Promise<string | null> => {
      const cachedDocument =
        documentCacheRef.current.get(documentType);

      if (cachedDocument) {
        return cachedDocument;
      }

      const existingRequest =
        inFlightRequestRef.current.get(documentType);

      if (existingRequest) {
        return existingRequest;
      }

      const requestPromise = (async () => {
        try {
          const requestPayload: DocumentRequest = {
            appId: application.appId,
            refId: application.refId ?? '',
            email: documentEmail,
            documentType,
            Language:
              i18n.language == Locale.EN
                ? Language.ENG
                : Language.BMY,
            preferredLang: getPreferredLang(
              i18n.language,
            ),
          };

          const response = await postDocument(
            requestPayload,
          ).unwrap();

          const documentData = response?.filePDF;

          if (
            documentData &&
            isBase64PDF(documentData)
          ) {
            documentCacheRef.current.set(
              documentType,
              documentData,
            );
            return documentData;
          }

          return null;
        } catch {
          if (redirectOnError) {
            redirect('error');
          }
          return null;
        } finally {
          inFlightRequestRef.current.delete(
            documentType,
          );
        }
      })();

      inFlightRequestRef.current.set(
        documentType,
        requestPromise,
      );

      return requestPromise;
    },
    [
      application.appId,
      documentEmail,
      application.refId,
      i18n.language,
      postDocument,
      redirect,
    ],
  );

  const prefetchDocuments = useCallback(
    async (documentTypes: DocumentType[]) => {
      await Promise.allSettled(
        documentTypes.map((documentType) =>
          fetchDocument(documentType, false),
        ),
      );
    },
    [fetchDocument],
  );

  const handleDocumentRequest = useCallback(
    async (documentType: DocumentType) => {
      const documentData = await fetchDocument(
        documentType,
        true,
      );

      if (documentData) {
        OpenPDF({
          filename: `${documentType}.pdf`,
          data: documentData,
          download: true,
        });
      }
    },
    [fetchDocument],
  );

  return {
    handleDocumentRequest,
    prefetchDocuments,
    isLoading,
  };
};
